import React, { useState } from 'react';
import {
  DecoratorNode,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

export interface SteelmanPayload {
  targetIdeology: string;
  content: string;
}

export type SerializedSteelmanNode = Spread<
  { targetIdeology: string; content: string },
  SerializedLexicalNode
>;

const IDEOLOGY_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  progressive: { bg: '#dbeafe', border: '#2563eb', text: '#1e40af' },
  conservative: { bg: '#fee2e2', border: '#dc2626', text: '#991b1b' },
  centrist: { bg: '#ede9fe', border: '#7c3aed', text: '#5b21b6' },
  libertarian: { bg: '#fef3c7', border: '#d97706', text: '#92400e' },
  populist: { bg: '#fff7ed', border: '#ea580c', text: '#9a3412' },
  stakeholder: { bg: '#d1fae5', border: '#059669', text: '#065f46' },
  expert: { bg: '#e0e7ff', border: '#4f46e5', text: '#3730a3' },
};

function SteelmanComponent({
  targetIdeology,
  content,
}: {
  targetIdeology: string;
  content: string;
  nodeKey: string;
}) {
  const [expanded, setExpanded] = useState(true);
  const colors = IDEOLOGY_COLORS[targetIdeology.toLowerCase()] || IDEOLOGY_COLORS.centrist;

  return (
    <div
      style={{
        backgroundColor: colors.bg,
        borderLeft: `4px solid ${colors.border}`,
        borderRadius: '8px',
        padding: '16px 20px',
        margin: '24px 0',
      }}
    >
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          gap: '8px',
          fontWeight: 600,
          color: colors.text,
          fontSize: '14px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        <span style={{ fontSize: '12px' }}>{expanded ? '▼' : '▶'}</span>
        <span>Best case for the {targetIdeology} position</span>
      </div>
      {expanded && (
        <div style={{ marginTop: '12px', color: '#374151', lineHeight: 1.7, fontSize: '16px' }}>
          {content}
        </div>
      )}
    </div>
  );
}

export class SteelmanNode extends DecoratorNode<React.ReactElement> {
  __targetIdeology: string;
  __content: string;

  static getType(): string {
    return 'steelman';
  }

  static clone(node: SteelmanNode): SteelmanNode {
    return new SteelmanNode(node.__targetIdeology, node.__content, node.__key);
  }

  constructor(targetIdeology: string, content: string, key?: NodeKey) {
    super(key);
    this.__targetIdeology = targetIdeology;
    this.__content = content;
  }

  static importJSON(serialized: SerializedSteelmanNode): SteelmanNode {
    return new SteelmanNode(serialized.targetIdeology, serialized.content);
  }

  exportJSON(): SerializedSteelmanNode {
    return {
      type: 'steelman',
      version: 1,
      targetIdeology: this.__targetIdeology,
      content: this.__content,
    };
  }

  createDOM(): HTMLElement {
    const div = document.createElement('div');
    div.className = 'steelman-node';
    return div;
  }

  updateDOM(): boolean {
    return false;
  }

  decorate(): React.ReactElement {
    return (
      <SteelmanComponent
        targetIdeology={this.__targetIdeology}
        content={this.__content}
        nodeKey={this.__key}
      />
    );
  }
}

export function $createSteelmanNode(payload: SteelmanPayload): SteelmanNode {
  return new SteelmanNode(payload.targetIdeology, payload.content);
}

export function $isSteelmanNode(node: LexicalNode): node is SteelmanNode {
  return node instanceof SteelmanNode;
}
